import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppLanguage } from '@/core/hooks/useAppLanguage';
import { BdsChipTag, BdsIcon, BdsIllustration } from '@/core/constants/webComponents';
import { getAccountId } from '@/core/utils/extractCookieId';
import userService from '@/core/services/UserService';
import panelCreditsService, { NormalizedPanelCreditOption, PanelCreditOption } from '@/core/services/PanelCreditsService';
import setupWizardService, { SetupWizardNamedKeywordsGroup, SetupWizardSuggestionsResponse } from '@/core/services/SetupWizardService';
import {
  ACTIVE_CHANNEL_IDS,
  ActiveChannelsState,
  AdvancedSettingsState,
  CreatePanelSuccessModal,
  EditActiveChannelsModal,
  EditAdvancedSettingsModal,
  EditCompetitorsModal,
  EditContextModal,
  EditLanguagesModal,
} from './modals';
import { Header } from '@/features/header/pages/Header';
import { NEW_SETUP_EVENTS } from './constants/events';
import offlineRobotIllustration from './assets/robot-6-offline.svg';
import './NewSetupApp.scss';

interface SetupFormState {
  brandName: string;
  brandDescription: string;
  panelName: string;
  trackingGoal: string;
}

interface CollectionCreditOptions {
  storageOptions: NormalizedPanelCreditOption[];
  slaOptions: NormalizedPanelCreditOption[];
}

interface SelectedCreditsPayload {
  storage: { quotationItemId: string };
  sla: { quotationItemId: string };
}

interface AmbiguousKeywordSuggestion {
  term: string;
  suggested: string;
  ambiguous: boolean;
  senses: string[];
  rationale: string;
}

type KeywordSuggestion = string | AmbiguousKeywordSuggestion;

interface NamedKeywordsGroup {
  name: string;
  keywords: string[];
}

interface GeneratedSetupResponse {
  brand: string;
  universeTitle: string;
  contextKeywords: KeywordSuggestion[];
  contextSuggestions: KeywordSuggestion[];
  tags: string[];
  tagSuggestions: string[];
  groups: NamedKeywordsGroup[];
  competitors: NamedKeywordsGroup[];
  themes: NamedKeywordsGroup[];
}

interface CreatePanelEventPayload {
  languages: string[];
  activeChannels: string[];
  generatedSetup: LegacyGeneratedSetup;
  advancedSettings: LegacyAdvancedSettings;
}

interface CreatePanelEventDetail {
  requestId: string;
  source: 'ai-stilingue-frontend';
  payload: CreatePanelEventPayload;
}

interface CreatePanelSuccessDetail {
  requestId?: string;
  [key: string]: unknown;
}

interface CreatedPanelNavigationEventDetail {
  requestId: string;
  source: 'ai-stilingue-frontend';
  creationResult: CreatePanelSuccessDetail;
}

type SetupOptionsSection =
  | 'context'
  | 'competitors'
  | 'groups'
  | 'themes'
  | 'tags'
  | 'languages'
  | 'activeChannels'
  | 'advancedSettings';

interface SetupOptionsSnapshot {
  context: string[];
  competitors: NamedKeywordsGroup[];
  groups: NamedKeywordsGroup[];
  themes: NamedKeywordsGroup[];
  tags: string[];
  languages: string[];
  activeChannels: string[];
  advancedSettings: AdvancedSettingsState;
}

type SetupOptionsSectionValueMap = {
  context: string[];
  competitors: NamedKeywordsGroup[];
  groups: NamedKeywordsGroup[];
  themes: NamedKeywordsGroup[];
  tags: string[];
  languages: string[];
  activeChannels: string[];
  advancedSettings: AdvancedSettingsState;
};

type SetupOptionsSectionValue = SetupOptionsSectionValueMap[SetupOptionsSection];

interface SetupOptionsUpdatedEventDetail {
  requestId: string;
  source: 'ai-stilingue-frontend';
  changedAt: string;
  section: SetupOptionsSection;
  suggested: SetupOptionsSectionValue;
  edited: SetupOptionsSectionValue;
}

interface LegacyNamedKeywordsGroup {
  title: string;
  name: string;
  descriptors: string[];
  keywords: string[];
  languages: string[];
  isBrand?: boolean;
}

interface LegacyGeneratedSetup {
  universeTitle: string;
  contextDescriptors: string[];
  monitoringOnlyContext: boolean;
  allowUniverseOnly: boolean;
  contextLanguages: string[];
  languages: string[];
  universeLanguages: string[];
  activeChannels: string[];
  channels: string[];
  tags: string[];
  interests: string[];
  blogs: string[];
  groups: LegacyNamedKeywordsGroup[];
  competitors: LegacyNamedKeywordsGroup[];
  themes: LegacyNamedKeywordsGroup[];
  monitoringProfiles: [];
}

interface LegacyAdvancedSettings {
  status: string;
  limitMentions: string;
  initialDate: string;
  finalDate: string;
  selectedCredits?: SelectedCreditsPayload;
  settingSwitches: {
    antispam: boolean;
    autoSentiment: boolean;
    neutral: boolean;
    retweets: boolean;
    snlp3: boolean;
  };
}

interface LanguagesState {
  pt: boolean;
  en: boolean;
  es: boolean;
}

type GenerateSearchFailureType = 'error' | 'offline';

const OFFLINE_RETRY_SECONDS = 30;

const INITIAL_FORM_STATE: SetupFormState = {
  brandName: '',
  brandDescription: '',
  panelName: '',
  trackingGoal: '',
};

const INITIAL_LANGUAGES_STATE: LanguagesState = {
  pt: true,
  en: false,
  es: false,
};

const buildInitialActiveChannels = (): ActiveChannelsState =>
  ACTIVE_CHANNEL_IDS.reduce((state, channelId) => {
    state[channelId] = true;
    return state;
  }, {} as ActiveChannelsState);

const DEFAULT_ADVANCED_START_DATE = '2026/07/22 - 14:26';

const buildInitialAdvancedSettings = (panelName: string): AdvancedSettingsState => ({
  antiSpam: true,
  autoSentiment: true,
  includeNeutral: true,
  processRetweets: true,
  startDate: DEFAULT_ADVANCED_START_DATE,
  panelName,
});

const normalizeString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeString(item))
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeKeywordSuggestion = (value: unknown): KeywordSuggestion | null => {
  if (typeof value === 'string') {
    const keyword = value.trim();
    if (!keyword) {
      return null;
    }

    return keyword;
  }

  if (value == null || typeof value !== 'object') {
    return null;
  }

  const rawKeyword = value as AmbiguousKeywordSuggestion;
  const term = normalizeString(rawKeyword.term);
  const suggested = normalizeString(rawKeyword.suggested);

  if (!term && !suggested) {
    return null;
  }

  return {
    term: term || suggested,
    suggested: suggested || term,
    ambiguous: Boolean(rawKeyword.ambiguous),
    senses: normalizeStringArray(rawKeyword.senses),
    rationale: normalizeString(rawKeyword.rationale),
  };
};

const normalizeKeywordSuggestions = (value: unknown): KeywordSuggestion[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => normalizeKeywordSuggestion(item))
    .filter((item): item is KeywordSuggestion => item !== null);
};

const normalizeNamedKeywordsGroups = (value: unknown): NamedKeywordsGroup[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((group, index) => {
    if (typeof group === 'string') {
      const normalizedName = normalizeString(group) || `Group ${index + 1}`;
      return { name: normalizedName, keywords: [] };
    }

    const rawGroup = (group ?? {}) as SetupWizardNamedKeywordsGroup;
    const name = normalizeString(rawGroup.name || rawGroup.title) || `Group ${index + 1}`;
    const keywords = normalizeStringArray(rawGroup.keywords ?? rawGroup.terms);

    return {
      name,
      keywords,
    };
  });
};

const normalizeCollectionCreditOptions = (value: unknown): NormalizedPanelCreditOption[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (item == null || typeof item !== 'object') {
      return [];
    }

    const option = item as PanelCreditOption;
    const name = normalizeString(option.productName) || normalizeString(option.name);

    if (name.length === 0) {
      return [];
    }

    const normalizedExpirationDate = normalizeString(option.expirationDate);
    let expirationTimestamp: number | null = null;

    if (typeof option.expirationDate === 'number') {
      expirationTimestamp = option.expirationDate;
    } else if (/^\d{13}$/.test(normalizedExpirationDate)) {
      expirationTimestamp = Number(normalizedExpirationDate);
    }

    const expirationDate = expirationTimestamp === null
      ? normalizedExpirationDate
      : new Date(expirationTimestamp).toLocaleDateString();
    const quotationItemId = normalizeString(option.quotationItemId);

    return [{
      name,
      expirationDate,
      ...(quotationItemId ? { quotationItemId } : {}),
    }];
  });
};

const buildGeneratedSetupResponse = (
  response: SetupWizardSuggestionsResponse,
  payload: SetupFormState,
): GeneratedSetupResponse => {
  const contextSuggestions = normalizeKeywordSuggestions(response.contextSuggestions ?? response.contextKeywords);
  const contextKeywords = normalizeKeywordSuggestions(response.contextKeywords ?? response.contextSuggestions);
  const tags = normalizeStringArray(response.tags);
  const tagSuggestions = normalizeStringArray(response.tagSuggestions ?? response.tags);

  return {
    brand: normalizeString(response.brand) || payload.brandName.trim(),
    universeTitle: payload.panelName.trim() || normalizeString(response.universeTitle),
    contextKeywords: contextKeywords.length > 0 ? contextKeywords : contextSuggestions,
    contextSuggestions: contextSuggestions.length > 0 ? contextSuggestions : contextKeywords,
    tags,
    tagSuggestions: tagSuggestions.length > 0 ? tagSuggestions : tags,
    groups: normalizeNamedKeywordsGroups(response.groups),
    competitors: normalizeNamedKeywordsGroups(response.competitors),
    themes: normalizeNamedKeywordsGroups(response.themes),
  };
};

const toKeywordLabel = (keyword: KeywordSuggestion): string => {
  if (typeof keyword === 'string') {
    return keyword;
  }

  return keyword.suggested || keyword.term;
};

const buildExpandedState = (items: NamedKeywordsGroup[]): Record<string, boolean> =>
  items.reduce<Record<string, boolean>>((expandedState, item) => {
    expandedState[item.name] = true;
    return expandedState;
  }, {});

const cloneNamedKeywordsGroups = (groups: NamedKeywordsGroup[]): NamedKeywordsGroup[] =>
  groups.map((group) => ({
    name: group.name,
    keywords: [...group.keywords],
  }));

const cloneSetupOptionsSnapshot = (snapshot: SetupOptionsSnapshot): SetupOptionsSnapshot => ({
  context: [...snapshot.context],
  competitors: cloneNamedKeywordsGroups(snapshot.competitors),
  groups: cloneNamedKeywordsGroups(snapshot.groups),
  themes: cloneNamedKeywordsGroups(snapshot.themes),
  tags: [...snapshot.tags],
  languages: [...snapshot.languages],
  activeChannels: [...snapshot.activeChannels],
  advancedSettings: { ...snapshot.advancedSettings },
});

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((item, index) => item === right[index]);

const areNamedKeywordsGroupsEqual = (left: NamedKeywordsGroup[], right: NamedKeywordsGroup[]): boolean =>
  left.length === right.length &&
  left.every(
    (group, index) => group.name === right[index]?.name && areStringArraysEqual(group.keywords, right[index]?.keywords ?? []),
  );

const areAdvancedSettingsEqual = (left: AdvancedSettingsState, right: AdvancedSettingsState): boolean =>
  left.antiSpam === right.antiSpam &&
  left.autoSentiment === right.autoSentiment &&
  left.includeNeutral === right.includeNeutral &&
  left.processRetweets === right.processRetweets &&
  left.startDate === right.startDate &&
  left.panelName === right.panelName;

const getSetupOptionsSectionValue = (
  snapshot: SetupOptionsSnapshot,
  section: SetupOptionsSection,
): SetupOptionsSectionValue => {
  if (section === 'context') {
    return [...snapshot.context];
  }

  if (section === 'competitors') {
    return cloneNamedKeywordsGroups(snapshot.competitors);
  }

  if (section === 'groups') {
    return cloneNamedKeywordsGroups(snapshot.groups);
  }

  if (section === 'themes') {
    return cloneNamedKeywordsGroups(snapshot.themes);
  }

  if (section === 'tags') {
    return [...snapshot.tags];
  }

  if (section === 'languages') {
    return [...snapshot.languages];
  }

  if (section === 'activeChannels') {
    return [...snapshot.activeChannels];
  }

  return { ...snapshot.advancedSettings };
};

const isSectionChangedFromSuggested = (
  section: SetupOptionsSection,
  suggested: SetupOptionsSnapshot,
  edited: SetupOptionsSnapshot,
): boolean => {
  if (section === 'context') {
    return !areStringArraysEqual(suggested.context, edited.context);
  }

  if (section === 'competitors') {
    return !areNamedKeywordsGroupsEqual(suggested.competitors, edited.competitors);
  }

  if (section === 'groups') {
    return !areNamedKeywordsGroupsEqual(suggested.groups, edited.groups);
  }

  if (section === 'themes') {
    return !areNamedKeywordsGroupsEqual(suggested.themes, edited.themes);
  }

  if (section === 'tags') {
    return !areStringArraysEqual(suggested.tags, edited.tags);
  }

  if (section === 'languages') {
    return !areStringArraysEqual(suggested.languages, edited.languages);
  }

  if (section === 'activeChannels') {
    return !areStringArraysEqual(suggested.activeChannels, edited.activeChannels);
  }

  return !areAdvancedSettingsEqual(suggested.advancedSettings, edited.advancedSettings);
};

const normalizeKeywords = (keywords: string[]): string[] => {
  const uniqueKeywords = new Set<string>();

  return keywords.reduce<string[]>((result, keyword) => {
    const trimmedKeyword = keyword.trim();

    if (!trimmedKeyword) {
      return result;
    }

    const normalizedKey = trimmedKeyword.toLowerCase();

    if (uniqueKeywords.has(normalizedKey)) {
      return result;
    }

    uniqueKeywords.add(normalizedKey);
    result.push(trimmedKeyword);
    return result;
  }, []);
};

const buildCreatePanelRequestId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const randomBytes = new Uint32Array(2);
    globalThis.crypto.getRandomValues(randomBytes);
    return `${randomBytes[0].toString(16)}-${randomBytes[1].toString(16)}`;
  }

  return `${Date.now()}`;
};

const getSelectedLanguageCodes = (languages: LanguagesState): string[] =>
  Object.entries(languages)
    .filter(([, isSelected]) => isSelected)
    .map(([languageCode]) => languageCode);

const getSelectedChannelIds = (channels: ActiveChannelsState): string[] =>
  ACTIVE_CHANNEL_IDS.filter((channelId) => channels[channelId]);

const buildSetupOptionsSnapshot = (
  generatedSetup: GeneratedSetupResponse,
  languages: LanguagesState,
  activeChannels: ActiveChannelsState,
  advancedSettings: AdvancedSettingsState,
): SetupOptionsSnapshot => ({
  context: generatedSetup.contextSuggestions.map(toKeywordLabel),
  competitors: cloneNamedKeywordsGroups(generatedSetup.competitors),
  groups: cloneNamedKeywordsGroups(generatedSetup.groups),
  themes: cloneNamedKeywordsGroups(generatedSetup.themes),
  tags: [...generatedSetup.tagSuggestions],
  languages: getSelectedLanguageCodes(languages),
  activeChannels: getSelectedChannelIds(activeChannels),
  advancedSettings: { ...advancedSettings },
});

const formatLegacyDate = (value: string): string => {
  const compactDate = value.replaceAll(/\D/g, '');

  return compactDate.length >= 12 ? compactDate.slice(0, 12) : value;
};

const toLegacyNamedKeywordsGroup = (
  group: NamedKeywordsGroup,
  languages: string[],
  isBrand?: boolean,
): LegacyNamedKeywordsGroup => ({
  title: group.name,
  name: group.name,
  descriptors: group.keywords,
  keywords: group.keywords,
  languages,
  ...(isBrand === undefined ? {} : { isBrand }),
});

const buildLegacyCreatePanelPayload = (
  generatedSetup: GeneratedSetupResponse,
  languages: LanguagesState,
  activeChannels: ActiveChannelsState,
  advancedSettings: AdvancedSettingsState,
  selectedCredits?: SelectedCreditsPayload,
): CreatePanelEventPayload => {
  const languageCodes = getSelectedLanguageCodes(languages);
  const channelIds = getSelectedChannelIds(activeChannels);
  const contextDescriptors = generatedSetup.contextSuggestions.map(toKeywordLabel);

  return {
    languages: languageCodes,
    activeChannels: channelIds,
    generatedSetup: {
      universeTitle: generatedSetup.universeTitle,
      contextDescriptors,
      monitoringOnlyContext: false,
      allowUniverseOnly: true,
      contextLanguages: languageCodes,
      languages: languageCodes,
      universeLanguages: languageCodes,
      activeChannels: channelIds,
      channels: channelIds,
      tags: generatedSetup.tagSuggestions,
      interests: [],
      blogs: [],
      groups: generatedSetup.groups.map((group) => toLegacyNamedKeywordsGroup(group, languageCodes)),
      competitors: generatedSetup.competitors.map((group) => toLegacyNamedKeywordsGroup(group, languageCodes, true)),
      themes: generatedSetup.themes.map((group) => toLegacyNamedKeywordsGroup(group, languageCodes)),
      monitoringProfiles: [],
    },
    advancedSettings: {
      status: 'Em execução',
      limitMentions: '',
      initialDate: formatLegacyDate(advancedSettings.startDate),
      finalDate: '',
      ...(selectedCredits ? { selectedCredits } : {}),
      settingSwitches: {
        antispam: advancedSettings.antiSpam,
        autoSentiment: advancedSettings.autoSentiment,
        neutral: advancedSettings.includeNeutral,
        retweets: advancedSettings.processRetweets,
        snlp3: false,
      },
    },
  };
};

const getNewSetupHostElement = (): HTMLElement | null =>
  globalThis.document.querySelector('content-new-setup');

// Visibility/positioning for the custom element host lives in the "new-setup-host" CSS class.
const NEW_SETUP_HOST_CLASS_NAME = 'new-setup-host';

interface GeneratedChipsSectionProps {
  title: string;
  chips: string[];
  editLabel: string;
  onEdit?: () => void;
}

const GeneratedChipsSection: React.FC<GeneratedChipsSectionProps> = ({ title, chips, editLabel, onEdit }) => (
  <section className="new-setup__generated-section">
    <div className="new-setup__generated-section-header">
      <h3 className="new-setup__generated-section-title">{title}</h3>
      <button type="button" className="new-setup__generated-edit-button" onClick={onEdit}>
        <BdsIcon theme="outline" name="edit" size="small" aria-hidden="true" />
        <span>{editLabel}</span>
      </button>
    </div>
    <div className="new-setup__chip-list">
      {chips.map((chip) => (
        <BdsChipTag key={chip} color="outline">
          {chip}
        </BdsChipTag>
      ))}
    </div>
  </section>
);

interface GeneratedGroupedSectionProps {
  title: string;
  items: NamedKeywordsGroup[];
  expandedState: Record<string, boolean>;
  onToggle: (itemName: string) => void;
  editLabel: string;
  onEdit?: () => void;
}

interface CollectionCreditSelectorProps {
  id: string;
  label: string;
  options: NormalizedPanelCreditOption[];
  expirationLabel: string;
  emptyPlaceholder: string;
  selectedOptionName: string;
  hasError: boolean;
  errorMessage: string;
  onSelect: (optionName: string) => void;
}

const CollectionCreditSelector: React.FC<CollectionCreditSelectorProps> = ({
  id,
  label,
  options,
  expirationLabel,
  emptyPlaceholder,
  selectedOptionName,
  hasError,
  errorMessage,
  onSelect,
}) => {
  const hasSelectedOption = options.some((option) => option.name === selectedOptionName);
  let placeholderText = '';

  if (hasSelectedOption) {
    placeholderText = selectedOptionName;
  } else if (options.length === 0) {
    placeholderText = emptyPlaceholder;
  }
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="new-setup__collection-selector">
      <button
        type="button"
        id={`${id}-trigger`}
        className={`new-setup__collection-trigger${hasError ? ' new-setup__collection-trigger--error' : ''}`}
        aria-expanded={isOpen}
        aria-controls={`${id}-options`}
        aria-describedby={hasError ? `${id}-error` : undefined}
        onClick={() => {
          if (options.length > 0) {
            setIsOpen((currentValue) => !currentValue);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setIsOpen(false);
          }
        }}
      >
        <span className="new-setup__collection-trigger-text">
          <span className="new-setup__collection-label">{label}</span>
          <span className="new-setup__collection-select-row">
            <span className={`new-setup__collection-value${options.length === 0 ? ' new-setup__collection-value--placeholder' : ''}`}>
              {hasSelectedOption ? selectedOptionName : placeholderText}
            </span>
            <span className="new-setup__collection-arrow" aria-hidden="true">
              <BdsIcon theme="outline" name={isOpen ? 'arrow-up' : 'arrow-down'} size="small" aria-hidden="true" />
            </span>
          </span>
        </span>
      </button>
      <div
        id={`${id}-options`}
        className="new-setup__collection-options"
        hidden={!isOpen || options.length === 0}
      >
        <ul className="new-setup__collection-option-list" aria-label={label}>
          {options.map((option, index) => (
            <li key={`${option.name}-${index}`}>
              <button
                type="button"
                className="new-setup__collection-option"
                aria-pressed={hasSelectedOption && selectedOptionName === option.name}
                onClick={() => {
                  onSelect(option.name);
                  setIsOpen(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setIsOpen(false);
                  }
                }}
              >
                <span className="new-setup__collection-option-name">{option.name}</span>
                {option.expirationDate && (
                  <span className="new-setup__collection-option-expiration">
                    {expirationLabel}: {option.expirationDate}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {hasError && (
        <p id={`${id}-error`} className="new-setup__collection-error-message">
          <span className="new-setup__collection-error-icon" aria-hidden="true">
            <BdsIcon theme="outline" name="close" size="small" aria-hidden="true" />
          </span>
          <span>{errorMessage}</span>
        </p>
      )}
    </div>
  );
};

const GeneratedGroupedSection: React.FC<GeneratedGroupedSectionProps> = ({
  title,
  items,
  expandedState,
  onToggle,
  editLabel,
  onEdit,
}) => (
  <section className="new-setup__generated-section">
    <div className="new-setup__generated-section-header">
      <h3 className="new-setup__generated-section-title">
        {title} ({items.length})
      </h3>
      <button type="button" className="new-setup__generated-edit-button" onClick={onEdit}>
        <BdsIcon theme="outline" name="edit" size="small" aria-hidden="true" />
        <span>{editLabel}</span>
      </button>
    </div>

    <div className="new-setup__generated-card-list">
      {items.map((item) => {
        const isExpanded = expandedState[item.name] ?? true;

        return (
          <article key={item.name} className="new-setup__generated-card">
            <button
              type="button"
              className="new-setup__generated-card-header"
              onClick={() => onToggle(item.name)}
              aria-expanded={isExpanded}
            >
              <span className="new-setup__generated-card-title">{item.name}</span>
              <span className="new-setup__generated-card-header-right">
                <span className="new-setup__generated-card-count">{item.keywords.length}</span>
                <span className="new-setup__generated-card-arrow" aria-hidden="true">
                  <BdsIcon theme="outline" name={isExpanded ? 'arrow-up' : 'arrow-down'} size="small" aria-hidden="true" />
                </span>
              </span>
            </button>

            {isExpanded && (
              <div className="new-setup__generated-card-body">
                <div className="new-setup__chip-list">
                  {item.keywords.map((keyword) => (
                    <BdsChipTag key={`${item.name}-${keyword}`} color="outline">
                      {keyword}
                    </BdsChipTag>
                  ))}
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  </section>
);

export const NewSetupApp: React.FC = () => {
  useAppLanguage();
  const { t } = useTranslation();
  const [formState, setFormState] = useState<SetupFormState>(INITIAL_FORM_STATE);
  const [hasManualPanelNameEdit, setHasManualPanelNameEdit] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateSearchFailure, setGenerateSearchFailure] = useState<GenerateSearchFailureType | null>(null);
  const [offlineRetrySeconds, setOfflineRetrySeconds] = useState(OFFLINE_RETRY_SECONDS);
  const [generatedSetup, setGeneratedSetup] = useState<GeneratedSetupResponse | null>(null);
  const [expandedCompetitors, setExpandedCompetitors] = useState<Record<string, boolean>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedThemes, setExpandedThemes] = useState<Record<string, boolean>>({});
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [isCompetitorsModalOpen, setIsCompetitorsModalOpen] = useState(false);
  const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
  const [isThemesModalOpen, setIsThemesModalOpen] = useState(false);
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
  const [isLanguagesModalOpen, setIsLanguagesModalOpen] = useState(false);
  const [isActiveChannelsModalOpen, setIsActiveChannelsModalOpen] = useState(false);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [isCreatingPanel, setIsCreatingPanel] = useState(false);
  const [createPanelRequestId, setCreatePanelRequestId] = useState<string | null>(null);
  const [createdPanel, setCreatedPanel] = useState<{ requestId: string; result: CreatePanelSuccessDetail } | null>(null);
  const [showCreatePanelErrorToast, setShowCreatePanelErrorToast] = useState(false);
  const [isCreatePanelSuccessModalOpen, setIsCreatePanelSuccessModalOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<LanguagesState>(INITIAL_LANGUAGES_STATE);
  const [collectionCreditOptions, setCollectionCreditOptions] = useState<CollectionCreditOptions | null>(null);
  const [selectedStorageOptionName, setSelectedStorageOptionName] = useState('');
  const [selectedSlaOptionName, setSelectedSlaOptionName] = useState('');
  const [showCollectionCreditErrors, setShowCollectionCreditErrors] = useState(false);
  const [showCollectionCreditValidationToast, setShowCollectionCreditValidationToast] = useState(false);
  const [activeChannels, setActiveChannels] = useState<ActiveChannelsState>(() => buildInitialActiveChannels());
  const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettingsState>(() =>
    buildInitialAdvancedSettings(INITIAL_FORM_STATE.panelName),
  );
  const [setupOptionsRequestId, setSetupOptionsRequestId] = useState<string | null>(null);
  const [setupOptionsSuggestedSnapshot, setSetupOptionsSuggestedSnapshot] = useState<SetupOptionsSnapshot | null>(null);

  const hasRequiredFields = Boolean(formState.brandName.trim() && formState.panelName.trim());
  const isGenerateDisabled = isGenerating || !hasRequiredFields;

  const clearSetupOptionsTracking = () => {
    setSetupOptionsRequestId(null);
    setSetupOptionsSuggestedSnapshot(null);
  };

  // The MFE is always open once mounted; the host element only needs the visibility class applied.
  useEffect(() => {
    getNewSetupHostElement()?.classList.add(NEW_SETUP_HOST_CLASS_NAME);
  }, []);

  const handleFieldChange =
    (field: keyof SetupFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextFieldValue = event.target.value;

      if (field === 'brandName') {
        setFormState((currentState) => ({
          ...currentState,
          brandName: nextFieldValue,
          panelName: hasManualPanelNameEdit ? currentState.panelName : nextFieldValue,
        }));
        return;
      }

      if (field === 'panelName') {
        setHasManualPanelNameEdit(true);
      }

      setFormState((currentState) => ({
        ...currentState,
        [field]: nextFieldValue,
      }));
    };

  const handleBack = () => {
    setGeneratedSetup(null);
    setIsContextModalOpen(false);
    setIsCompetitorsModalOpen(false);
    setIsGroupsModalOpen(false);
    setIsThemesModalOpen(false);
    setIsTagsModalOpen(false);
    setIsLanguagesModalOpen(false);
    setIsActiveChannelsModalOpen(false);
    setIsAdvancedModalOpen(false);
    setShowCreatePanelErrorToast(false);
    setShowCollectionCreditErrors(false);
    setShowCollectionCreditValidationToast(false);
    setSelectedStorageOptionName('');
    setSelectedSlaOptionName('');
    setIsCreatePanelSuccessModalOpen(false);
    setIsCreatingPanel(false);
    setCreatePanelRequestId(null);
    setCreatedPanel(null);
    setGenerateSearchFailure(null);
    setOfflineRetrySeconds(OFFLINE_RETRY_SECONDS);
    clearSetupOptionsTracking();
  };

  const handleRestart = () => {
    setGeneratedSetup(null);
    setFormState(INITIAL_FORM_STATE);
    setHasManualPanelNameEdit(false);
    setExpandedCompetitors({});
    setExpandedGroups({});
    setExpandedThemes({});
    setIsContextModalOpen(false);
    setIsCompetitorsModalOpen(false);
    setIsGroupsModalOpen(false);
    setIsThemesModalOpen(false);
    setIsTagsModalOpen(false);
    setIsLanguagesModalOpen(false);
    setIsActiveChannelsModalOpen(false);
    setIsAdvancedModalOpen(false);
    setIsCreatingPanel(false);
    setCreatePanelRequestId(null);
    setCreatedPanel(null);
    setShowCreatePanelErrorToast(false);
    setShowCollectionCreditErrors(false);
    setShowCollectionCreditValidationToast(false);
    setSelectedStorageOptionName('');
    setSelectedSlaOptionName('');
    setIsCreatePanelSuccessModalOpen(false);
    setSelectedLanguages(INITIAL_LANGUAGES_STATE);
    setActiveChannels(buildInitialActiveChannels());
    setAdvancedSettings(buildInitialAdvancedSettings(INITIAL_FORM_STATE.panelName));
    setGenerateSearchFailure(null);
    setOfflineRetrySeconds(OFFLINE_RETRY_SECONDS);
    clearSetupOptionsTracking();
  };

  const handleGenerateSearchReload = () => {
    globalThis.location.reload();
  };

  const startGenerateSearch = useCallback(async () => {
    if (!hasRequiredFields || isGenerating) {
      return;
    }

    setGenerateSearchFailure(null);

    const isOnline = globalThis.navigator.onLine;

    if (isOnline === false) {
      setGeneratedSetup(null);
      setGenerateSearchFailure('offline');
      setOfflineRetrySeconds(OFFLINE_RETRY_SECONDS);
      clearSetupOptionsTracking();
      return;
    }

    setIsGenerating(true);

    try {
      const response = await setupWizardService.getSuggestions({
        brand: formState.brandName.trim(),
        brandDescription: formState.brandDescription.trim(),
        focus: formState.trackingGoal.trim(),
      });

      const generatedSetupResponse = buildGeneratedSetupResponse(response, formState);
      const nextAdvancedSettings = buildInitialAdvancedSettings(generatedSetupResponse.universeTitle);
      const nextSetupOptionsRequestId = buildCreatePanelRequestId();
      const suggestedSnapshot = buildSetupOptionsSnapshot(
        generatedSetupResponse,
        selectedLanguages,
        activeChannels,
        nextAdvancedSettings,
      );

      setGeneratedSetup(generatedSetupResponse);
      setExpandedCompetitors(buildExpandedState(generatedSetupResponse.competitors));
      setExpandedGroups(buildExpandedState(generatedSetupResponse.groups));
      setExpandedThemes(buildExpandedState(generatedSetupResponse.themes));
      setAdvancedSettings(nextAdvancedSettings);
      setSetupOptionsRequestId(nextSetupOptionsRequestId);
      setSetupOptionsSuggestedSnapshot(suggestedSnapshot);
    } catch {
      setGeneratedSetup(null);
      clearSetupOptionsTracking();

      if (globalThis.navigator.onLine) {
        setGenerateSearchFailure('error');
      } else {
        setGenerateSearchFailure('offline');
        setOfflineRetrySeconds(OFFLINE_RETRY_SECONDS);
      }
    } finally {
      setIsGenerating(false);
    }
  }, [
    activeChannels,
    formState,
    hasRequiredFields,
    isGenerating,
    selectedLanguages,
  ]);

  const emitSetupOptionsUpdatedEvent = useCallback((section: SetupOptionsSection) => {
    if (!generatedSetup || !setupOptionsSuggestedSnapshot || !setupOptionsRequestId) {
      return;
    }

    const suggestedSnapshot = cloneSetupOptionsSnapshot(setupOptionsSuggestedSnapshot);
    const editedSnapshot = buildSetupOptionsSnapshot(
      generatedSetup,
      selectedLanguages,
      activeChannels,
      advancedSettings,
    );

    if (!isSectionChangedFromSuggested(section, suggestedSnapshot, editedSnapshot)) {
      return;
    }

    const suggestedSection = getSetupOptionsSectionValue(suggestedSnapshot, section);
    const editedSection = getSetupOptionsSectionValue(editedSnapshot, section);

    const detail: SetupOptionsUpdatedEventDetail = {
      requestId: setupOptionsRequestId,
      source: 'ai-stilingue-frontend',
      changedAt: new Date().toISOString(),
      section,
      suggested: suggestedSection,
      edited: editedSection,
    };

    document.dispatchEvent(
      new CustomEvent<SetupOptionsUpdatedEventDetail>(NEW_SETUP_EVENTS.SETUP_OPTIONS_UPDATED, {
        detail,
      }),
    );
  }, [
    activeChannels,
    advancedSettings,
    generatedSetup,
    selectedLanguages,
    setupOptionsRequestId,
    setupOptionsSuggestedSnapshot,
  ]);

  useEffect(() => {
    let isActive = true;
    const loadCollectionCredits = async () => {
      const accountId = getAccountId();
      if (!accountId || accountId === 'DEFAULT') {
        console.info('[NewSetup] Skipping CentralLimitsService.getLimitByAccountId: account ID is unavailable');
        return;
      }

      try {
        console.info('[NewSetup] Calling UserService.getAccountById');
        const accountResponse = await userService.getAccountById(accountId);
        if (!isActive) {
          return;
        }

        const userGroup = accountResponse.userGroup;
        console.info('[NewSetup] Account values received', {
          accountId,
          accountName: accountResponse.name,
          userGroup,
        });

        if (!userGroup) {
          console.info('[NewSetup] Skipping UserService.getCompany: account usergroup is unavailable');
          return;
        }

        console.info('[NewSetup] Calling UserService.getCompany');
        const company = await userService.getCompany(userGroup);
        if (!isActive) {
          return;
        }

        console.info('[NewSetup] UserService.getCompany completed', {
          useUniverseCreditSystem: company.useUniverseCreditSystem === true,
        });

        if (company.useUniverseCreditSystem !== true) {
          console.info('[NewSetup] Skipping PanelCreditsService.getPanelCredits: credit system is disabled');
          return;
        }

        console.info('[NewSetup] Calling PanelCreditsService.getPanelCredits');
        const credits = await panelCreditsService.getPanelCredits(accountId, userGroup);
        if (isActive) {
          const availableStorageOptions = normalizeCollectionCreditOptions(credits.available?.storage);
          const availableSlaOptions = normalizeCollectionCreditOptions(credits.available?.sla);
          const storageOptions = availableStorageOptions.length
            ? availableStorageOptions
            : normalizeCollectionCreditOptions(credits.storageOptions);
          const slaOptions = availableSlaOptions.length
            ? availableSlaOptions
            : normalizeCollectionCreditOptions(credits.slaOptions);

          setCollectionCreditOptions({ storageOptions, slaOptions });
          console.info('[NewSetup] PanelCreditsService.getPanelCredits completed', {
            storageOptionsCount: storageOptions.length,
            slaOptionsCount: slaOptions.length,
          });
        }
      } catch (error) {
        console.error(
          '[NewSetup] Credit service request failed',
          error instanceof Error ? error.message : 'Unknown error',
        );
        if (isActive) {
          setCollectionCreditOptions(null);
        }
      }
    };

    void loadCollectionCredits();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (generateSearchFailure !== 'offline' || isGenerating) {
      return;
    }

    const countdownInterval = globalThis.setInterval(() => {
      setOfflineRetrySeconds((currentSeconds) => Math.max(currentSeconds - 1, 0));
    }, 1000);

    return () => {
      globalThis.clearInterval(countdownInterval);
    };
  }, [generateSearchFailure, isGenerating]);

  useEffect(() => {
    if (generateSearchFailure !== 'offline') {
      return;
    }

    const handleOnline = () => {
      setOfflineRetrySeconds(OFFLINE_RETRY_SECONDS);
      void startGenerateSearch();
    };

    globalThis.addEventListener('online', handleOnline);

    return () => {
      globalThis.removeEventListener('online', handleOnline);
    };
  }, [generateSearchFailure, startGenerateSearch]);

  useEffect(() => {
    if (generateSearchFailure !== 'offline' || isGenerating || offlineRetrySeconds > 0) {
      return;
    }

    if (globalThis.navigator.onLine) {
      void startGenerateSearch();
      return;
    }

    setOfflineRetrySeconds(OFFLINE_RETRY_SECONDS);
  }, [generateSearchFailure, isGenerating, offlineRetrySeconds, startGenerateSearch]);

  useEffect(() => {
    const handleCreatePanelSuccess = (event: Event) => {
      const detail = (event as CustomEvent<CreatePanelSuccessDetail>).detail ?? {};
      const { requestId } = detail;

      if (createPanelRequestId && requestId && requestId !== createPanelRequestId) {
        return;
      }

      if (!isCreatingPanel) {
        return;
      }

      setIsCreatingPanel(false);
      setCreatePanelRequestId(null);
      setCreatedPanel({
        requestId: requestId ?? createPanelRequestId ?? '',
        result: detail,
      });
      setShowCreatePanelErrorToast(false);
      setIsCreatePanelSuccessModalOpen(true);
    };

    const handleCreatePanelError = (event: Event) => {
      const detail = (event as CustomEvent<CreatePanelSuccessDetail>).detail ?? {};
      const { requestId } = detail;

      if (createPanelRequestId && requestId && requestId !== createPanelRequestId) {
        return;
      }

      if (!isCreatingPanel) {
        return;
      }

      setIsCreatingPanel(false);
      setCreatePanelRequestId(null);
      setShowCreatePanelErrorToast(true);
    };

    document.addEventListener(NEW_SETUP_EVENTS.CREATE_PANEL_SUCCESS, handleCreatePanelSuccess);
    document.addEventListener(NEW_SETUP_EVENTS.CREATE_PANEL_ERROR, handleCreatePanelError);

    return () => {
      document.removeEventListener(NEW_SETUP_EVENTS.CREATE_PANEL_SUCCESS, handleCreatePanelSuccess);
      document.removeEventListener(NEW_SETUP_EVENTS.CREATE_PANEL_ERROR, handleCreatePanelError);
    };
  }, [createPanelRequestId, isCreatingPanel]);

  const activeChannelsCount = useMemo(
    () => ACTIVE_CHANNEL_IDS.filter((channelId) => activeChannels[channelId]).length,
    [activeChannels],
  );

  const activeChannelsSummary = useMemo(
    () => t('newSetup.channelsModal.sectionValue', { active: activeChannelsCount, total: ACTIVE_CHANNEL_IDS.length }),
    [activeChannelsCount, t],
  );

  const storageOptions = collectionCreditOptions?.storageOptions ?? [];
  const slaOptions = collectionCreditOptions?.slaOptions ?? [];
  const hasLoadedCollectionCredits = collectionCreditOptions !== null;

  const handleStorageOptionSelect = (optionName: string) => {
    setSelectedStorageOptionName(optionName);
    if (slaOptions.some((option) => option.name === selectedSlaOptionName)) {
      setShowCollectionCreditValidationToast(false);
    }
  };

  const handleSlaOptionSelect = (optionName: string) => {
    setSelectedSlaOptionName(optionName);
    if (storageOptions.some((option) => option.name === selectedStorageOptionName)) {
      setShowCollectionCreditValidationToast(false);
    }
  };

  const advancedSummary = useMemo(
    () =>
      `${t('newSetup.advancedModal.summary.antiSpam')} ${
        advancedSettings.antiSpam ? t('newSetup.advancedModal.summary.on') : t('newSetup.advancedModal.summary.off')
      } . ${t('newSetup.advancedModal.summary.sentiment')} ${
        advancedSettings.autoSentiment ? t('newSetup.advancedModal.summary.on') : t('newSetup.advancedModal.summary.off')
      } . ${t('newSetup.advancedModal.summary.neutral')} ${
        advancedSettings.includeNeutral ? t('newSetup.advancedModal.summary.on') : t('newSetup.advancedModal.summary.off')
      } . ${t('newSetup.advancedModal.summary.retweets')} ${
        advancedSettings.processRetweets ? t('newSetup.advancedModal.summary.on') : t('newSetup.advancedModal.summary.off')
      } . ${t('newSetup.advancedModal.summary.start')} ${advancedSettings.startDate}`,
    [advancedSettings, t],
  );

  const selectedLanguagesLabel = useMemo(() => {
    const selectedCodes = ['PT'];

    if (selectedLanguages.en) {
      selectedCodes.push('EN');
    }

    if (selectedLanguages.es) {
      selectedCodes.push('ES');
    }

    return selectedCodes.join(', ');
  }, [selectedLanguages]);

  const contextKeywords = useMemo(() => {
    if (!generatedSetup) {
      return [];
    }

    return generatedSetup.contextSuggestions.map(toKeywordLabel);
  }, [generatedSetup]);

  const tagsAsGroup = useMemo<NamedKeywordsGroup[]>(() => {
    if (!generatedSetup) {
      return [];
    }

    return [
      {
        name: t('newSetup.tagsModal.defaultGroupName'),
        keywords: generatedSetup.tagSuggestions,
      },
    ];
  }, [generatedSetup, t]);

  const updateContextKeywords = (nextKeywords: string[]) => {
    setGeneratedSetup((currentSetup) => {
      if (!currentSetup) {
        return currentSetup;
      }

      return {
        ...currentSetup,
        contextSuggestions: nextKeywords,
        contextKeywords: nextKeywords,
      };
    });
  };

  const updateCompetitors = (nextCompetitors: NamedKeywordsGroup[]) => {
    setGeneratedSetup((currentSetup) => {
      if (!currentSetup) {
        return currentSetup;
      }

      return {
        ...currentSetup,
        competitors: nextCompetitors,
      };
    });

    setExpandedCompetitors(buildExpandedState(nextCompetitors));
  };

  const updateGroups = (nextGroups: NamedKeywordsGroup[]) => {
    setGeneratedSetup((currentSetup) => {
      if (!currentSetup) {
        return currentSetup;
      }

      return {
        ...currentSetup,
        groups: nextGroups,
      };
    });

    setExpandedGroups(buildExpandedState(nextGroups));
  };

  const updateThemes = (nextThemes: NamedKeywordsGroup[]) => {
    setGeneratedSetup((currentSetup) => {
      if (!currentSetup) {
        return currentSetup;
      }

      return {
        ...currentSetup,
        themes: nextThemes,
      };
    });

    setExpandedThemes(buildExpandedState(nextThemes));
  };

  const updateTags = (nextTagGroups: NamedKeywordsGroup[]) => {
    const nextTags = normalizeKeywords(nextTagGroups.flatMap((group) => group.keywords));

    setGeneratedSetup((currentSetup) => {
      if (!currentSetup) {
        return currentSetup;
      }

      return {
        ...currentSetup,
        tags: nextTags,
        tagSuggestions: nextTags,
      };
    });
  };

  const openModalWhenSetupIsReady = (setModalState: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (!generatedSetup) {
      return;
    }

    setModalState(true);
  };

  const openContextModal = () => openModalWhenSetupIsReady(setIsContextModalOpen);

  const closeContextModal = () => {
    setIsContextModalOpen(false);
  };

  const finishContextModal = () => {
    emitSetupOptionsUpdatedEvent('context');
  };

  const openCompetitorsModal = () => openModalWhenSetupIsReady(setIsCompetitorsModalOpen);

  const closeCompetitorsModal = () => {
    setIsCompetitorsModalOpen(false);
  };

  const finishCompetitorsModal = () => {
    emitSetupOptionsUpdatedEvent('competitors');
  };

  const openGroupsModal = () => openModalWhenSetupIsReady(setIsGroupsModalOpen);

  const closeGroupsModal = () => {
    setIsGroupsModalOpen(false);
  };

  const finishGroupsModal = () => {
    emitSetupOptionsUpdatedEvent('groups');
  };

  const openThemesModal = () => openModalWhenSetupIsReady(setIsThemesModalOpen);

  const closeThemesModal = () => {
    setIsThemesModalOpen(false);
  };

  const finishThemesModal = () => {
    emitSetupOptionsUpdatedEvent('themes');
  };

  const openTagsModal = () => openModalWhenSetupIsReady(setIsTagsModalOpen);

  const closeTagsModal = () => {
    setIsTagsModalOpen(false);
  };

  const finishTagsModal = () => {
    emitSetupOptionsUpdatedEvent('tags');
  };

  const openLanguagesModal = () => openModalWhenSetupIsReady(setIsLanguagesModalOpen);

  const closeLanguagesModal = () => {
    setIsLanguagesModalOpen(false);
  };

  const finishLanguagesModal = () => {
    emitSetupOptionsUpdatedEvent('languages');
  };

  const openActiveChannelsModal = () => openModalWhenSetupIsReady(setIsActiveChannelsModalOpen);

  const closeActiveChannelsModal = () => {
    setIsActiveChannelsModalOpen(false);
  };

  const finishActiveChannelsModal = () => {
    emitSetupOptionsUpdatedEvent('activeChannels');
  };

  const openAdvancedModal = () => openModalWhenSetupIsReady(setIsAdvancedModalOpen);

  const closeAdvancedModal = () => {
    setIsAdvancedModalOpen(false);
  };

  const finishAdvancedModal = () => {
    emitSetupOptionsUpdatedEvent('advancedSettings');
  };

  const updateAdvancedSettings = (nextSettings: AdvancedSettingsState) => {
    setAdvancedSettings(nextSettings);

    setGeneratedSetup((currentSetup) => {
      if (!currentSetup) {
        return currentSetup;
      }

      return {
        ...currentSetup,
        universeTitle: nextSettings.panelName,
      };
    });
  };

  const closeCreatePanelSuccessModal = () => {
    setIsCreatePanelSuccessModalOpen(false);
  };

  const handleContinueEditing = () => {
    if (!createdPanel) {
      return;
    }

    const detail: CreatedPanelNavigationEventDetail = {
      requestId: createdPanel.requestId,
      source: 'ai-stilingue-frontend',
      creationResult: createdPanel.result,
    };

    document.dispatchEvent(
      new CustomEvent<CreatedPanelNavigationEventDetail>(NEW_SETUP_EVENTS.CONTINUE_EDITING, {
        detail,
      }),
    );

    setIsCreatePanelSuccessModalOpen(false);

    const continueEditingPath = normalizeString(createdPanel.result.continueEditingPath);

    if (continueEditingPath) {
      const url = new URL(continueEditingPath, globalThis.location.origin);
      if (url.origin === globalThis.location.origin) {
        globalThis.location.assign(url.toString());
      }
    }
  };

  const handleGoToSearch = () => {
    if (!createdPanel) {
      return;
    }

    const detail: CreatedPanelNavigationEventDetail = {
      requestId: createdPanel.requestId,
      source: 'ai-stilingue-frontend',
      creationResult: createdPanel.result,
    };

    document.dispatchEvent(
      new CustomEvent<CreatedPanelNavigationEventDetail>(NEW_SETUP_EVENTS.GO_TO_SEARCH, {
        detail,
      }),
    );

    setIsCreatePanelSuccessModalOpen(false);

    const searchPath = normalizeString(createdPanel.result.searchPath);

    if (searchPath) {
      globalThis.location.assign(searchPath);
    }
  };

  const handleCreatePanel = () => {
    if (!generatedSetup || isCreatingPanel) {
      return;
    }

    let selectedCredits: SelectedCreditsPayload | undefined;

    if (collectionCreditOptions) {
      const selectedStorageCredit = storageOptions.find((option) => option.name === selectedStorageOptionName);
      const selectedSlaCredit = slaOptions.find((option) => option.name === selectedSlaOptionName);

      if (!selectedStorageCredit || !selectedSlaCredit) {
        setShowCollectionCreditErrors(true);
        setShowCollectionCreditValidationToast(true);
        return;
      }

      if (selectedStorageCredit.quotationItemId && selectedSlaCredit.quotationItemId) {
        selectedCredits = {
          storage: { quotationItemId: selectedStorageCredit.quotationItemId },
          sla: { quotationItemId: selectedSlaCredit.quotationItemId },
        };
      }
    }

    setShowCreatePanelErrorToast(false);
    setIsCreatingPanel(true);

    const requestId = buildCreatePanelRequestId();
    setCreatePanelRequestId(requestId);

    const payload = buildLegacyCreatePanelPayload(
      generatedSetup,
      selectedLanguages,
      activeChannels,
      advancedSettings,
      selectedCredits,
    );

    const detail: CreatePanelEventDetail = {
      requestId,
      source: 'ai-stilingue-frontend',
      payload,
    };

    document.dispatchEvent(
      new CustomEvent<CreatePanelEventDetail>(NEW_SETUP_EVENTS.CREATE_PANEL, {
        detail,
      }),
    );
  };

  const updateLanguages = (nextLanguages: LanguagesState) => {
    setSelectedLanguages({
      ...nextLanguages,
      pt: true,
    });
  };

  const toggleExpandedItem = (
    itemName: string,
    expandedState: Record<string, boolean>,
    setExpandedState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  ) => {
    const isExpanded = expandedState[itemName] ?? true;
    setExpandedState((currentExpandedState) => ({
      ...currentExpandedState,
      [itemName]: !isExpanded,
    }));
  };

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    await startGenerateSearch();
  };

  const renderGeneratedSetupContent = () => (
    <div className="new-setup__generated-content">
      <div className="new-setup__generated-title-block">
        <h2 className="new-setup__generated-title">{t('newSetup.generated.title')}</h2>
        <p className="new-setup__generated-subtitle">{t('newSetup.generated.subtitle')}</p>
      </div>

      <section className="new-setup__generated-section">
        <h3 className="new-setup__generated-section-title">{t('newSetup.generated.panelName')}</h3>
        <p className="new-setup__generated-panel-name">{generatedSetup?.universeTitle}</p>
      </section>

      <GeneratedChipsSection
        title={t('newSetup.generated.context')}
        chips={generatedSetup?.contextSuggestions.map(toKeywordLabel) ?? []}
        editLabel={t('newSetup.generated.edit')}
        onEdit={openContextModal}
      />

      <GeneratedGroupedSection
        title={t('newSetup.generated.competitors')}
        items={generatedSetup?.competitors ?? []}
        expandedState={expandedCompetitors}
        onToggle={(itemName) => toggleExpandedItem(itemName, expandedCompetitors, setExpandedCompetitors)}
        editLabel={t('newSetup.generated.edit')}
        onEdit={openCompetitorsModal}
      />

      <GeneratedGroupedSection
        title={t('newSetup.generated.groups')}
        items={generatedSetup?.groups ?? []}
        expandedState={expandedGroups}
        onToggle={(itemName) => toggleExpandedItem(itemName, expandedGroups, setExpandedGroups)}
        editLabel={t('newSetup.generated.edit')}
        onEdit={openGroupsModal}
      />

      <GeneratedGroupedSection
        title={t('newSetup.generated.themes')}
        items={generatedSetup?.themes ?? []}
        expandedState={expandedThemes}
        onToggle={(itemName) => toggleExpandedItem(itemName, expandedThemes, setExpandedThemes)}
        editLabel={t('newSetup.generated.edit')}
        onEdit={openThemesModal}
      />

      <GeneratedChipsSection
        title={t('newSetup.generated.tags')}
        chips={generatedSetup?.tagSuggestions ?? []}
        editLabel={t('newSetup.generated.edit')}
        onEdit={openTagsModal}
      />

      <div className="new-setup__generated-columns">
        <section className="new-setup__generated-section new-setup__generated-section--column">
          <div className="new-setup__generated-section-header">
            <h3 className="new-setup__generated-section-title">{t('newSetup.generated.languages')}</h3>
            <button type="button" className="new-setup__generated-edit-button" onClick={openLanguagesModal}>
              <BdsIcon theme="outline" name="edit" size="small" aria-hidden="true" />
              <span>{t('newSetup.generated.edit')}</span>
            </button>
          </div>
          <p className="new-setup__generated-simple-value">{selectedLanguagesLabel}</p>
        </section>

        <section className="new-setup__generated-section new-setup__generated-section--column">
          <div className="new-setup__generated-section-header">
            <h3 className="new-setup__generated-section-title">{t('newSetup.generated.activeChannels')}</h3>
            <button type="button" className="new-setup__generated-edit-button" onClick={openActiveChannelsModal}>
              <BdsIcon theme="outline" name="edit" size="small" aria-hidden="true" />
              <span>{t('newSetup.generated.edit')}</span>
            </button>
          </div>
          <p className="new-setup__generated-simple-value">{activeChannelsSummary}</p>
        </section>
      </div>

      <section className="new-setup__generated-section">
        <div className="new-setup__generated-section-header">
          <h3 className="new-setup__generated-section-title">{t('newSetup.generated.advanced')}</h3>
          <button type="button" className="new-setup__generated-edit-button" onClick={openAdvancedModal}>
            <BdsIcon theme="outline" name="edit" size="small" aria-hidden="true" />
            <span>{t('newSetup.generated.edit')}</span>
          </button>
        </div>
        <p className="new-setup__generated-simple-value">{advancedSummary}</p>
      </section>

      {hasLoadedCollectionCredits && (
        <section className="new-setup__collection-section" aria-labelledby="new-setup-collection-title">
          <div className="new-setup__collection-heading">
            <h3 id="new-setup-collection-title" className="new-setup__generated-section-title">
              {t('newSetup.collection.title')}
            </h3>
            <p className="new-setup__generated-simple-value">{t('newSetup.collection.description')}</p>
          </div>
          <div className="new-setup__collection-selectors">
            <CollectionCreditSelector
              id="storage"
              label={t('newSetup.collection.storage')}
              options={storageOptions}
              expirationLabel={t('newSetup.collection.expiration')}
              emptyPlaceholder={t('newSetup.collection.emptyPlaceholder')}
              selectedOptionName={selectedStorageOptionName}
              hasError={showCollectionCreditErrors && !selectedStorageOptionName}
              errorMessage={t('newSetup.collection.storageRequired')}
              onSelect={handleStorageOptionSelect}
            />
            <CollectionCreditSelector
              id="sla"
              label={t('newSetup.collection.sla')}
              options={slaOptions}
              expirationLabel={t('newSetup.collection.expiration')}
              emptyPlaceholder={t('newSetup.collection.emptyPlaceholder')}
              selectedOptionName={selectedSlaOptionName}
              hasError={showCollectionCreditErrors && !selectedSlaOptionName}
              errorMessage={t('newSetup.collection.slaRequired')}
              onSelect={handleSlaOptionSelect}
            />
          </div>
        </section>
      )}

      <button
        type="button"
        className={`new-setup__create-button${isCreatingPanel ? ' new-setup__create-button--loading' : ''}`}
        disabled={isCreatingPanel}
        onClick={handleCreatePanel}
      >
        {isCreatingPanel ? (
          <span className="new-setup__generate-loader" aria-hidden="true" />
        ) : (
          <span className="new-setup__create-icon" aria-hidden="true">
            <BdsIcon theme="outline" name="sparkle-ai" size="medium" aria-hidden="true" />
          </span>
        )}
        <span>{isCreatingPanel ? t('newSetup.generated.creatingPanel') : t('newSetup.generated.createPanel')}</span>
      </button>
    </div>
  );

  const renderGenerateSearchFailureContent = () => {
    if (!generateSearchFailure) {
      return null;
    }

    const isOfflineFailure = generateSearchFailure === 'offline';
    const baseTranslationKey = isOfflineFailure ? 'newSetup.generateSearchOffline' : 'newSetup.generateSearchError';
    const failureDescription = isOfflineFailure
      ? t(`${baseTranslationKey}.description`, { seconds: offlineRetrySeconds })
      : t(`${baseTranslationKey}.description`);

    return (
      <section
        className={`new-setup__generate-search-error${isOfflineFailure ? ' new-setup__generate-search-error--offline' : ''}`}
        aria-live="polite"
      >
        <div className="new-setup__generate-search-error-body">
          <div className="new-setup__generate-search-error-illustration-wrap" aria-hidden="true">
            {isOfflineFailure ? (
              <img
                src={offlineRobotIllustration}
                alt={t(`${baseTranslationKey}.title`)}
                className="new-setup__generate-search-error-illustration"
              />
            ) : (
              <BdsIllustration
                type="default"
                name="atention"
                alt={t(`${baseTranslationKey}.title`)}
                class="new-setup__generate-search-error-illustration"
              />
            )}
          </div>

          <div className="new-setup__generate-search-error-content">
            <div className="new-setup__generate-search-error-header">
              <h2 className="new-setup__generate-search-error-title">{t(`${baseTranslationKey}.title`)}</h2>
                <p className="new-setup__generate-search-error-description">{failureDescription}</p>
            </div>

            <div className="new-setup__generate-search-error-actions">
              <button
                type="button"
                className="new-setup__generate-search-error-retry-button"
                onClick={handleGenerateSearchReload}
              >
                {t(`${baseTranslationKey}.button`)}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderSetupFormContent = () => (
    <form className="new-setup__content" onSubmit={handleSubmit}>
      <div className="new-setup__title-block">
        <h2 className="new-setup__title">{t('newSetup.title')}</h2>
        <p className="new-setup__subtitle">{t('newSetup.subtitle')}</p>
      </div>

      <div className="new-setup__field">
        <label className="new-setup__label" htmlFor="new-setup-brand-name">
          {t('newSetup.brandNameLabel')}
        </label>
        <input
          id="new-setup-brand-name"
          className="new-setup__field-control"
          placeholder={t('newSetup.brandNamePlaceholder')}
          value={formState.brandName}
          required
          onChange={handleFieldChange('brandName')}
        />
      </div>

      <div className="new-setup__field">
        <label className="new-setup__label" htmlFor="new-setup-brand-description">
          {t('newSetup.brandDescriptionLabel')}
        </label>
        <textarea
          id="new-setup-brand-description"
          className="new-setup__field-control new-setup__field-control--textarea"
          placeholder={t('newSetup.brandDescriptionPlaceholder')}
          value={formState.brandDescription}
          onChange={handleFieldChange('brandDescription')}
        />
        <p className="new-setup__help-text">{t('newSetup.brandDescriptionHelp')}</p>
      </div>

      <div className="new-setup__field">
        <label className="new-setup__label" htmlFor="new-setup-panel-name">
          {t('newSetup.panelNameLabel')}
        </label>
        <input
          id="new-setup-panel-name"
          className="new-setup__field-control"
          placeholder={t('newSetup.panelNamePlaceholder')}
          value={formState.panelName}
          required
          onChange={handleFieldChange('panelName')}
        />
        <p className="new-setup__help-text">{t('newSetup.panelNameHelp')}</p>
      </div>

      <div className="new-setup__field">
        <label className="new-setup__label" htmlFor="new-setup-tracking-goal">
          {t('newSetup.trackingGoalLabel')}
        </label>
        <textarea
          id="new-setup-tracking-goal"
          className="new-setup__field-control new-setup__field-control--textarea"
          placeholder={t('newSetup.trackingGoalPlaceholder')}
          value={formState.trackingGoal}
          onChange={handleFieldChange('trackingGoal')}
        />
        <p className="new-setup__help-text">{t('newSetup.trackingGoalHelp')}</p>
      </div>

      <button
        type="submit"
        className={`new-setup__generate-button${isGenerating ? ' new-setup__generate-button--loading' : ''}`}
        disabled={isGenerateDisabled}
      >
        {isGenerating ? (
          <span className="new-setup__generate-loader" aria-hidden="true" />
        ) : (
          <span className="new-setup__generate-icon" aria-hidden="true">
            <BdsIcon theme="outline" name="sparkle-ai" size="medium" aria-hidden="true" />
          </span>
        )}
        <span>{isGenerating ? t('newSetup.generatingSearch') : t('newSetup.generateSearch')}</span>
      </button>
    </form>
  );

  const renderMainContent = () => {
    const failureContent = renderGenerateSearchFailureContent();

    if (generatedSetup) {
      return renderGeneratedSetupContent();
    }

    if (failureContent) {
      return failureContent;
    }

    return renderSetupFormContent();
  };

  const appClassName = ['new-setup', 'new-setup--open', 'new-setup--with-platform-header'].join(' ');

  return (
    <>
      <Header hideFilterBar hideSearchBar hideSidebarButton />
      <div className={appClassName} data-open="true">
      <section className="new-setup__card" aria-label={t('newSetup.builderTitle')}>
        <header className="new-setup__header">
          <div className="new-setup__brand">
            <span className="new-setup__brand-icon" aria-hidden="true">
              <span className="new-setup__brand-sparkle-icon" aria-hidden="true">
                <BdsIcon theme="solid" name="sparkle-ai" size="medium" aria-hidden="true" />
              </span>
            </span>
            <div>
              <h1 className="new-setup__brand-title">{t('newSetup.builderTitle')}</h1>
              <p className="new-setup__brand-subtitle">{t('newSetup.builderSubtitle')}</p>
            </div>
          </div>

          <div className="new-setup__header-actions">
            <button type="button" className="new-setup__header-button" onClick={handleBack}>
              <span className="new-setup__header-icon" aria-hidden="true">
                <BdsIcon theme="outline" name="undo" size="medium" aria-hidden="true" />
              </span>
              <span>{t('newSetup.back')}</span>
            </button>
            <button type="button" className="new-setup__header-button" onClick={handleRestart}>
              <span className="new-setup__header-icon" aria-hidden="true">
                <BdsIcon theme="outline" name="refresh" size="medium" aria-hidden="true" />
              </span>
              <span>{t('newSetup.restart')}</span>
            </button>
          </div>
        </header>

        {renderMainContent()}
      </section>

      {generatedSetup && (
        <EditContextModal
          isOpen={isContextModalOpen}
          keywords={contextKeywords}
          onChange={updateContextKeywords}
          onFinish={finishContextModal}
          onClose={closeContextModal}
        />
      )}

      {generatedSetup && isCompetitorsModalOpen && (
        <EditCompetitorsModal
          groups={generatedSetup.competitors}
          onChange={updateCompetitors}
          onFinish={finishCompetitorsModal}
          onClose={closeCompetitorsModal}
        />
      )}

      {generatedSetup && isGroupsModalOpen && (
        <EditCompetitorsModal
          groups={generatedSetup.groups}
          onChange={updateGroups}
          onFinish={finishGroupsModal}
          onClose={closeGroupsModal}
          translationPrefix="newSetup.groupsModal"
        />
      )}

      {generatedSetup && isThemesModalOpen && (
        <EditCompetitorsModal
          groups={generatedSetup.themes}
          onChange={updateThemes}
          onFinish={finishThemesModal}
          onClose={closeThemesModal}
          translationPrefix="newSetup.themesModal"
        />
      )}

      {generatedSetup && isTagsModalOpen && (
        <EditCompetitorsModal
          groups={tagsAsGroup}
          onChange={updateTags}
          onFinish={finishTagsModal}
          onClose={closeTagsModal}
          translationPrefix="newSetup.tagsModal"
          allowGroupManagement={false}
          showGroupNameField={false}
        />
      )}

      {generatedSetup && (
        <EditLanguagesModal
          isOpen={isLanguagesModalOpen}
          selectedLanguages={selectedLanguages}
          onChange={updateLanguages}
          onFinish={finishLanguagesModal}
          onClose={closeLanguagesModal}
        />
      )}

      {generatedSetup && (
        <EditActiveChannelsModal
          isOpen={isActiveChannelsModalOpen}
          selectedChannels={activeChannels}
          onChange={setActiveChannels}
          onFinish={finishActiveChannelsModal}
          onClose={closeActiveChannelsModal}
        />
      )}

      {generatedSetup && (
        <EditAdvancedSettingsModal
          isOpen={isAdvancedModalOpen}
          settings={advancedSettings}
          onChange={updateAdvancedSettings}
          onFinish={finishAdvancedModal}
          onClose={closeAdvancedModal}
        />
      )}

      {showCreatePanelErrorToast && (
        <section className="new-setup__create-panel-error-toast" role="alert" aria-live="assertive">
          <span className="new-setup__create-panel-error-toast-icon" aria-hidden="true">
            <BdsIcon theme="outline" name="close" size="small" aria-hidden="true" />
          </span>

          <div className="new-setup__create-panel-error-toast-texts">
            <h4 className="new-setup__create-panel-error-toast-title">{t('newSetup.createPanelErrorToast.title')}</h4>
            <p className="new-setup__create-panel-error-toast-description">{t('newSetup.createPanelErrorToast.description')}</p>
          </div>

          <button type="button" className="new-setup__create-panel-error-toast-action" onClick={handleCreatePanel}>
            {t('newSetup.createPanelErrorToast.retry')}
          </button>
        </section>
      )}

      {showCollectionCreditValidationToast && (
        <section className="new-setup__collection-validation-toast" role="alert" aria-live="assertive">
          <span className="new-setup__collection-validation-toast-icon" aria-hidden="true">
            <BdsIcon theme="outline" name="close" size="small" aria-hidden="true" />
          </span>
          <p className="new-setup__collection-validation-toast-message">
            {t('newSetup.collection.selectionRequired')}
          </p>
          <button
            type="button"
            className="new-setup__collection-validation-toast-close"
            aria-label={t('newSetup.collection.closeValidationToast')}
            onClick={() => setShowCollectionCreditValidationToast(false)}
          >
            <BdsIcon theme="outline" name="close" size="medium" aria-hidden="true" />
          </button>
        </section>
      )}

      <CreatePanelSuccessModal
        isOpen={isCreatePanelSuccessModalOpen}
        onClose={closeCreatePanelSuccessModal}
        onContinueEditing={handleContinueEditing}
        onGoToSearch={handleGoToSearch}
      />
      </div>
    </>
  );
};
